import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { DrawerLayoutWrapper } from 'common';
import InfoIcon from '@mui/icons-material/Info';
import ProgressBar from 'feature/dispatching/components/ProgressBar';
import SubmitProgressHeader from 'feature/dispatching/components/SubmitProgressHeader';
import ErrorHeader from './ErrorHeader';
import SubmittedTrailer from './SubmittedTrailer';

const Summary = ({ onClose, prev, reset, trailerState }) => {
  const { trailers } = trailerState;
  const { viewById: locationId } = useParams();

  const [progress, setProgress] = useState(0);
  const [totalProgress, setTotalProgress] = useState(0);
  const progressRate = 100 / trailers.length;
  const [showErrorHeader, setShowErrorHeader] = useState(false);

  const progressUpdateHandler = useCallback(errArg => {
    setProgress(R.inc);
    if (errArg?.error) {
      setShowErrorHeader(errArg.error);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setTotalProgress(Math.ceil(progressRate * progress));
    // eslint-disable-next-line
  }, [progress]);

  const handleAddMore = () => {
    if (totalProgress === 100) reset();
    else prev();
  };

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={handleAddMore} sx={{ width: 150 }} variant="outlined">
        Add More
      </Button>
      <Button onClick={onClose} sx={{ width: 150 }}>
        Done
      </Button>
    </Box>
  );

  const renderProgress =
    totalProgress === 100 && showErrorHeader ? (
      <ErrorHeader />
    ) : (
      <SubmitProgressHeader copy="All Steps Complete!" totalProgress={totalProgress} />
    );

  return (
    <DrawerLayoutWrapper
      buttons={buttons}
      headerButtonText="CLOSE"
      isLoading={false}
      onHeaderButtonClick={onClose}
      title="Adding Trailer"
    >
      <ProgressBar
        label="Trailers (2 of 2)"
        sx={{
          '&.MuiLinearProgress-colorPrimary > .MuiLinearProgress-bar': {
            borderRadius: '10px',
          },
        }}
        value={100}
      />
      <Stack sx={{ mt: 3, width: 390 }}>
        <Box bgcolor="graphic.lighter" color="primary.dark" px={3} py={2} width="100%">
          <Typography color="inherit" sx={{ pb: 1 }} variant="body1">
            Adding Trailers
          </Typography>
          {renderProgress}
        </Box>
        <Divider />
        {trailers.map(({ trailerId }) => {
          return (
            <SubmittedTrailer
              key={trailerId}
              locationId={parseInt(locationId)}
              trailerId={trailerId}
              updateHandler={progressUpdateHandler}
            />
          );
        })}
        {totalProgress === 100 && (
          <Box sx={{ mt: 2.5, pb: 3, px: 3 }}>
            <Alert
              icon={<InfoIcon fontSize="inherit" />}
              severity="info"
              sx={{ bgcolor: 'success.light' }}
              variant="trailerWizard"
            >
              It can take up to 48 hours until a Trailer can begin loading at a location.
            </Alert>
          </Box>
        )}
      </Stack>
    </DrawerLayoutWrapper>
  );
};

Summary.propTypes = {
  onClose: PropTypes.func.isRequired,
  prev: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  trailerState: PropTypes.shape({
    trailers: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default Summary;
